/**
 * Labstep
 *
 * @module grid/NewIndex/coldefs/resource-item/amount
 * @desc Column Definition for resource item amount
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type { CellRendererParams } from 'labstep-web/core/DataGrid/types';
import { setValue } from 'labstep-web/core/DataGrid/utils';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { PostFilterFieldTypes } from 'labstep-web/grid/services/grid-filter.service';
import type { Entity } from 'labstep-web/models/entity.model';
import { string } from 'yup';

export const colDefResourceItemAmount = {
  colId: 'amount',
  headerName: 'Amount Remaining',
  headerComponentParams: {
    icon: ICONS.resource_item.info.quantity,
    postFilterSettings: {
      fieldType: PostFilterFieldTypes.numeric,
      field: 'amount',
    },
  },
  editable: (params: EditableCallbackParams) =>
    GridNewIndexService.getEditable(params),
  valueGetter: (params: ValueGetterParams) => {
    if (params.node?.data?.amount === null) {
      return '';
    }
    return `${params.node?.data?.amount} ${params.node?.data?.unit || ''}`;
  },
  valueSetter: (params: any): boolean => {
    const newValue = params.newValue;
    if (
      newValue === null ||
      newValue === undefined ||
      newValue === ''
    ) {
      return false;
    }
    if (typeof newValue === 'string') {
      const regex = /(\d+(?:[.,]\d+)?)(\s*[a-zA-Zµ°±²³´¹ΩΔ\/]*)/;
      const match = newValue.replace(',', '.').match(regex);
      if (!match) {
        return false;
      }
      const amount = match[1];
      const unit = match[2].trim();
      setValue(
        'amount',
        amount,
        {
          entityName: params.data.entityName,
          id: params.data.id,
        },
        string().required(),
      );
      if (unit && unit.length > 0) {
        setValue(
          'unit',
          unit,
          {
            entityName: params.data.entityName,
            id: params.data.id,
          },
          string().required('Field must be a valid unit'),
        );
      }
      return true;
    } else if (typeof newValue === 'number') {
      setValue(
        'amount',
        newValue.toString(),
        {
          entityName: params.data.entityName,
          id: params.data.id,
        },
        string().required(),
      );
      return true;
    }
    return false;
  },
  suppressKeyboardEvent: suppressKeyboardEventClearCells,
  valueClearer: () => '0',
  cellRenderer: (params: CellRendererParams<Entity>) => {
    if (!params.data || params.data.amount === null) {
      return (
        <DataGridPlaceholder
          params={params}
          placeholder="Enter"
          editable={GridNewIndexService.getEditable(params)}
        />
      );
    }
    return `${params.data.amount || 0} ${params.data.unitDisplayValue || ''}`;
  },
};
